// src/components/game/play/GamePlay.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { 
  Sword, Shield, Heart, User, 
  Users, Map, MessageCircle, Menu,
  Backpack, Settings, LogOut,
  Scroll, DicesIcon, Star, 
  Info, AlertCircle, Save,
  ChevronRight, X, RefreshCw
} from 'lucide-react';
import { MOCK_GAME_MODULES, MOCK_GAME_STATE } from '../../../games/wintermere/mockData';
import './GamePlay.css';

const client = generateClient();

// Debug Panel Component
const DebugPanel = ({ gameModule, gameState, currentScene }) => (
  <div className="game-play__debug">
    <h3>Debug Information</h3>
    <pre>
      {JSON.stringify({
        gameModule: gameModule?.id,
        currentScene: currentScene?.id,
        gameState: {
          currentScene: gameState?.currentScene,
          storyFlags: gameState?.state ? JSON.parse(gameState.state).storyFlags : []
        }
      }, null, 2)}
    </pre>
  </div>
);

// Loading Spinner Component
const LoadingSpinner = ({ message }) => (
  <div className="game-play__loading">
    <RefreshCw className="game-play__loading-icon" />
    <div className="game-play__loading-message">{message}</div>
  </div>
);

// Error Display Component
const ErrorDisplay = ({ message, onRetry }) => (
  <div className="game-play__error">
    <AlertCircle size={24} />
    <div className="game-play__error-message">{message}</div>
    {onRetry && (
      <button className="game-play__error-retry" onClick={onRetry}>
        Try Again
      </button>
    )}
  </div>
);

// Toast Notification Component
const ToastNotification = ({ message, type, onClose }) => (
  <div className={`game-play__toast game-play__toast--${type}`}>
    <span>{message}</span>
    <button onClick={onClose} className="game-play__toast-close">
      <X size={16} />
    </button>
  </div>
);

const GamePlay = () => {
  const { gameId = 'wintermere' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  console.log('Route params:', { gameId });
  console.log('Location state:', location.state);

  // Core state management
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Initializing game...');
  const [error, setError] = useState(null);
  const [gameModule, setGameModule] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [currentScene, setCurrentScene] = useState(null);
  const [party, setParty] = useState(null);
  const [activeCharacter, setActiveCharacter] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showDebug, setShowDebug] = useState(false && process.env.NODE_ENV === 'development');

  // UI state
  const [showInventory, setShowInventory] = useState(false);
  const [showPartyStatus, setShowPartyStatus] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showChat, setShowChat] = useState(false);

  // Debug logging
  useEffect(() => {
    if (showDebug) {
      console.log('State Update:', {
        currentScene,
        gameState,
        gameModule
      });
    }
  }, [currentScene, gameState, gameModule, showDebug]);

  // Notification helper
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Fetch party members
  const fetchPartyMembers = useCallback(async () => {
    try {
      const gameStateId = gameState?.id;
      if (!gameState?.id || !client?.models?.CharacterGameState) {
        console.log('Missing required data for fetching party members');
        return;
      }
      
      const result = await client.models.CharacterGameState.list({
        filter: {
          gameStateId: {
            eq: gameStateId
          }
        }
      });
      
      setParty(result.data);
      const leader = result.data.find(char => 
        char.characterId === gameState?.partyLeaderId
      );
      if (leader) setActiveCharacter(leader);
    } catch (err) {
      console.error('Error fetching party members:', err);
      showNotification('Failed to update party information', 'error');
    }
  }, [gameState, showNotification]);

  // Load initial game data
  useEffect(() => {
    const loadGameData = async () => {
      try {
        setLoading(true);
        setLoadingMessage('Loading game module...');
        
        // Try to fetch game module
        let moduleData;
        try {
          console.log('Attempting to fetch game module for ID:', gameId);
          moduleData = MOCK_GAME_MODULES[gameId];
          console.log('Using mock module data:', moduleData);

          if (!moduleData) {
            const response = await client.models.GameModule.get({
              id: gameId
            });
            moduleData = response;
            console.log('Loaded module from API:', moduleData);
          }
        } catch (err) {
          console.error('Error fetching game module:', err);
          throw new Error(`Game module not found: ${gameId}`);
        }

        if (!moduleData) {
          throw new Error('No game module data available');
        }

        setGameModule(moduleData);
        
        // Parse scenes
        setLoadingMessage('Loading scenes...');
        console.log('Raw scenes data:', moduleData.scenes);
        const scenes = JSON.parse(moduleData.scenes);
        console.log('Parsed scenes:', scenes);

        // Create initial game state if none exists
        setLoadingMessage('Initializing game state...');
        const gameStateData = location.state?.gameState || MOCK_GAME_STATE;
        setGameState(gameStateData);

        // Validate that the current scene exists
        const targetScene = scenes[gameStateData.currentScene];
        if (!targetScene) {
            console.error(`Scene ${gameStateData.currentScene} not found, falling back to tavern_entrance`);
            gameStateData.currentScene = 'tavern_entrance';
        }

        setGameState(gameStateData);
        setCurrentScene(scenes[gameStateData.currentScene]);
        console.log('Set current scene:', scenes[gameStateData.currentScene]);

        // Load party if we have a game state
        if (gameStateData.id) {
          await fetchPartyMembers();
        }

      } catch (err) {
        console.error('Error loading game data:', err);
        setError(`Failed to load game: ${err.message}`);
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    };

    loadGameData();
  }, [gameId, location.state, fetchPartyMembers]);

  // Handle scene transitions
  const handleSceneAction = async (action) => {
    try {
      if (!gameModule || !gameState) {
        throw new Error('Game not properly initialized');
      }

      setLoadingMessage('Processing action...');
      setLoading(true);

      const scenes = JSON.parse(gameModule.scenes);
      const nextScene = scenes[action.nextScene];

      if (!nextScene) {
        throw new Error(`Scene not found: ${action.nextScene}`);
      }

      const newState = {
        ...gameState,
        currentScene: action.nextScene,
        state: JSON.stringify({
          ...JSON.parse(gameState.state),
          lastAction: action,
          timestamp: new Date().toISOString()
        })
      };

      // Update game state in database
      try {
        await client.models.GameState.save(newState);
      } catch (err) {
        console.log('Failed to save game state:', err);
        showNotification('Auto-save failed', 'warning');
      }

      setGameState(newState);
      setCurrentScene(nextScene);

      // Process rewards
      if (action.rewards) {
        await processRewards(action.rewards);
      }

      // Process character effects
      if (action.characterEffects) {
        await updateCharacterStates(action.characterEffects);
      }

      showNotification('Action completed', 'success');
    } catch (err) {
      console.error('Error processing action:', err);
      setError('Failed to process action. Please try again.');
      showNotification('Failed to process action', 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Process rewards
  const processRewards = async (rewards) => {
    try {
      if (!party) return;

      const updates = party.map(async (member) => {
        const currentState = await client.models.CharacterGameState.get({
          id: member.id
        });

        const newState = await client.models.CharacterGameState.update({
          id: member.id,
          experience: currentState.experience + (rewards.experience || 0),
          inventory: JSON.stringify([
            ...JSON.parse(currentState.inventory || '[]'),
            ...(rewards.items || [])
          ])
        });

        if (rewards.experience) {
          showNotification(`Gained ${rewards.experience} experience!`, 'success');
        }

        if (rewards.items?.length) {
          showNotification(`Received ${rewards.items.length} new item(s)!`, 'success');
        }

        return newState;
      });

      await Promise.all(updates);
      await fetchPartyMembers();
    } catch (err) {
      console.error('Error processing rewards:', err);
      showNotification('Failed to process rewards', 'error');
    }
  };

  // Update character states
  const updateCharacterStates = async (effects) => {
    try {
      if (!party) return;

      const updates = party.map(async (member) => {
        const effect = effects[member.id];
        if (!effect) return;

        const newState = await client.models.CharacterGameState.update({
          id: member.id,
          currentHealth: Math.max(0, member.currentHealth + (effect.healthChange || 0)),
          status: effect.status || member.status,
          individualState: JSON.stringify({
            ...JSON.parse(member.individualState || '{}'),
            ...effect.stateChanges
          })
        });

        if (effect.healthChange) {
          const message = effect.healthChange > 0 
            ? `${member.name} healed for ${effect.healthChange} HP`
            : `${member.name} took ${Math.abs(effect.healthChange)} damage`;
          showNotification(message, effect.healthChange > 0 ? 'success' : 'warning');
        }

        return newState;
      });

      await Promise.all(updates);
      await fetchPartyMembers();
    } catch (err) {
      console.error('Error updating character states:', err);
      showNotification('Failed to update character states', 'error');
    }
  };

  // Handle save game
  const handleSaveGame = async () => {
    try {
      if (!gameState) return;

      setLoadingMessage('Saving game...');
      setLoading(true);

      await client.models.GameState.save({
        ...gameState,
        updatedAt: new Date().toISOString()
      });

      showNotification('Game saved successfully', 'success');
    } catch (err) {
      console.error('Error saving game:', err);
      showNotification('Failed to save game', 'error');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Handle exit game
  const handleExitGame = async () => {
    try {
      await handleSaveGame();
      navigate('/games');
    } catch (err) {
      console.error('Error exiting game:', err);
      showNotification('Failed to save game before exit', 'error');
      // Navigate anyway
      navigate('/games');
    }
  };

  // Loading state check
  if (loading) {
    return <LoadingSpinner message={loadingMessage} />;
  }

  // Error state check
  if (error) {
    return (
      <ErrorDisplay 
        message={error} 
        onRetry={() => window.location.reload()} 
      />
    );
  }

  return (
    <div className="game-play">
      <header className="game-play__header">
        <h1 className="game-play__title">{gameModule?.name}</h1>
        <div className="game-play__controls">
          <button 
            className="game-play__control-button"
            onClick={() => setShowInventory(!showInventory)}
            aria-label="Toggle Inventory"
          >
            <Backpack />
            <span className="game-play__tooltip">Inventory</span>
          </button>
          <button 
            className="game-play__control-button"
            onClick={() => setShowPartyStatus(!showPartyStatus)}
            aria-label="Toggle Party Status"
          >
            <Users />
            <span className="game-play__tooltip">Party Status</span>
          </button>
          <button 
            className="game-play__control-button"
            onClick={() => setShowChat(!showChat)}
            aria-label="Toggle Chat"
          >
            <MessageCircle />
            <span className="game-play__tooltip">Chat</span>
          </button>
          <button 
            className="game-play__control-button"
            onClick={() => setShowSettings(!showSettings)}
            aria-label="Toggle Settings"
          >
            <Settings />
            <span className="game-play__tooltip">Settings</span>
          </button>
          <button 
            className="game-play__control-button"
            onClick={handleSaveGame}
            aria-label="Save Game"
          >
            <Save />
            <span className="game-play__tooltip">Save Game</span>
          </button>
          <button 
            className="game-play__control-button game-play__control-button--exit"
            onClick={handleExitGame}
            aria-label="Exit Game"
          >
            <LogOut />
            <span className="game-play__tooltip">Exit Game</span>
          </button>
        </div>
      </header>

      <main className="game-play__main">
        {showDebug && (
          <DebugPanel 
            gameModule={gameModule}
            gameState={gameState}
            currentScene={currentScene}
          />
        )}

        <div className="game-play__scene">
          {currentScene ? (
            <>
              <h2 className="game-play__scene-title">
                {currentScene.title}
              </h2>
              <div className="game-play__scene-description">
                {currentScene.description}
              </div>
              <div className="game-play__scene-actions">
                {currentScene.choices?.map((choice) => (
                  <button
                    key={choice.id}
                    className="game-play__action-button"
                    onClick={() => handleSceneAction(choice)}
                    disabled={choice.requires && 
                      !JSON.parse(gameState.state).storyFlags?.includes(choice.requires)}
                  >
                    <span className="game-play__action-button-text">
                      {choice.text}
                    </span>
                    {choice.requires && (
                      <span className="game-play__action-button-requirement">
                        Requires: {choice.requires}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="game-play__no-scene">
              <AlertCircle size={24} />
              <p>Scene not found</p>
            </div>
          )}
        </div>

        {showPartyStatus && (
          <div className="game-play__party-status">
            <h3 className="game-play__section-title">
              <Users size={20} />
              Party Status
            </h3>
            {party?.map((member) => (
              <div key={member.id} className="game-play__party-member">
                <div className="game-play__party-member-name">
                  <User size={16} />
                  {member.name}
                </div>
                <div className="game-play__party-member-health">
                  <Heart size={16} />
                  {member.currentHealth}/{member.maxHealth}
                </div>
                {member.status && (
                  <div className="game-play__party-member-status">
                    {member.status}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {showInventory && (
          <div className="game-play__inventory">
            <h3 className="game-play__section-title">
              <Backpack size={20} />
              Inventory
            </h3>
            {activeCharacter?.inventory ? (
              JSON.parse(activeCharacter.inventory).map((item, index) => (
                <div key={index} className="game-play__inventory-item">
                  {item.name}
                  {item.type && (
                    <span className="game-play__inventory-item-type">
                      {item.type}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <p className="game-play__empty-message">Inventory is empty</p>
            )}
          </div>
        )}

        {showSettings && (
          <div className="game-play__settings">
            <h3 className="game-play__section-title">
              <Settings size={20} />
              Settings
            </h3>
            <div className="game-play__settings-buttons">
              <button 
                className="game-play__settings-button"
                onClick={handleSaveGame}
              >
                <Save size={16} />
                Save Game
              </button>
              <button 
                className="game-play__settings-button"
                onClick={handleExitGame}
              >
                <LogOut size={16} />
                Exit to Menu
              </button>
              {process.env.NODE_ENV === 'development' && (
                <button 
                  className="game-play__settings-button"
                  onClick={() => setShowDebug(!showDebug)}
                >
                  <Info size={16} />
                  Toggle Debug Panel
                </button>
              )}
            </div>
          </div>
        )}

        {showChat && (
          <div className="game-play__chat">
            <h3 className="game-play__section-title">
              <MessageCircle size={20} />
              Chat
            </h3>
            <p className="game-play__coming-soon">Chat feature coming soon!</p>
          </div>
        )}
      </main>

      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default GamePlay;